import { ConfigObservable } from 'bz-stores/lib/config';
import { LicenseObservable } from 'bz-stores/lib/licence';
import GUID from 'bz-utilities/modules/variables/guid';
import { action, computed, observable } from 'mobx';
import Api from 'tm3-core/api';
import type { AccountingSettingInfo } from 'tm3-core/api.dtos';
import { AccountingSystemProviderName, PaymentProviderName } from 'tm3-core/api.dtos';

import { cache } from 'tm3-core/cache';
import FeatureEnum from 'tm3-core/enums/feature-enum';
import type IFeature from 'tm3-core/interfaces/feature';
import ModuleEnum from 'tm3-core/licence';

class FeatureStore {
  static readonly testRegex = /^true$/i;

  @observable loaded: boolean = false;
  flags = observable.map<FeatureEnum, string>();

  public load(): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.get('/api/sys/config/features')
        .then((res: any) => {
          this.flags.replace(res.body.result);
          this.loaded = true;
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  }

  @action.bound
  public hasFlag(feature: FeatureEnum): boolean {
    return this.has([feature]);
  }

  @action.bound
  public has(features: Array<FeatureEnum>, all: boolean = true): boolean {
    if (!this.loaded) {
      return false;
    }

    const fn = (x: FeatureEnum) => {
      const flag = this.flags.get(x) || 'false';
      return FeatureStore.testRegex.test(flag);
    };

    return !!all ? features.every(fn) : features.some(fn);
  }

  public match(value: IFeature, skipConfig: boolean = false): boolean {
    if (!this.loaded) {
      return false;
    }

    const { flags = [], config = {} } = value;
    if (!this.has(flags)) {
      return false;
    }
    if (skipConfig) return true;

    return Object.entries(config).every((x) => {
      let [key, value] = x;
      let val = ConfigObservable.config[key] || '';
      return val.toLowerCase() === value?.toLowerCase();
    });
  }

  @computed
  get isClick() {
    const { license } = LicenseObservable;
    return !!license && license.get('isClick');
  }

  @computed
  get isCaseflow() {
    const { license } = LicenseObservable;
    return license?.get('isCaseflow');
  }

  @computed
  get prontoIntegrationEnabled() {
    return this.loaded && this.has([FeatureEnum.PRONTO_INTEGRATION]);
  }

  @computed
  get upgradeEnabled(): boolean {
    return this.loaded && this.has([FeatureEnum.UPGRADE_ENABLED]);
  }

  @computed
  get paymentIntegrationAvailable() {
    return this.loaded && !cache.isProntoPortal;
  }

  @computed
  get square() {
    return (
      this.loaded &&
      (cache.hasAllowedModule(ModuleEnum.CardPayments) || this.isClick) &&
      this.has([FeatureEnum.SQUARE])
    );
  }

  @computed
  get userDiaryRestriction() {
    return this.loaded && this.has([FeatureEnum.USER_DIARY_RESTRICTION]);
  }

  @computed
  get stripeEnabled() {
    const {
      TM3_PAYMENTS_ENABLE_TM3,
      TM3_PAYMENTS_MERCHANT,
      STRIPE_TEMP_CSRF_STATE = ''
    } = ConfigObservable.config;

    return (
      this.paymentIntegrationAvailable &&
      TM3_PAYMENTS_ENABLE_TM3 === 'TRUE' &&
      STRIPE_TEMP_CSRF_STATE.length > 0 &&
      (TM3_PAYMENTS_MERCHANT.toLowerCase() === PaymentProviderName.Stripe.toLowerCase() ||
        TM3_PAYMENTS_MERCHANT.toLowerCase() === PaymentProviderName.None.toLowerCase())
    );
  }

  @computed
  get stripeExisitingClient() {
    const {
      TM3_PAYMENTS_ENABLE_TM3,
      TM3_PAYMENTS_MERCHANT,
      STRIPE_TEMP_CSRF_STATE = ''
    } = ConfigObservable.config;

    return (
      this.paymentIntegrationAvailable &&
      TM3_PAYMENTS_ENABLE_TM3 === 'TRUE' &&
      GUID.isValid(STRIPE_TEMP_CSRF_STATE) &&
      TM3_PAYMENTS_MERCHANT.toLowerCase() === PaymentProviderName.Stripe.toLowerCase()
    );
  }

  @computed
  get squareEnabled() {
    const { TM3_PAYMENTS_ENABLE_TM3, SQUARE_AUTHENTICATED, TM3_PAYMENTS_MERCHANT } =
      ConfigObservable.config;
    const { square } = this;
    return (
      square &&
      TM3_PAYMENTS_ENABLE_TM3 === 'TRUE' &&
      TM3_PAYMENTS_MERCHANT.toLowerCase() === PaymentProviderName.Square.toLowerCase() &&
      SQUARE_AUTHENTICATED === 'TRUE'
    );
  }

  @computed
  get clearAcceptEnabled() {
    const { TM3_PAYMENTS_ENABLE_TM3, TM3_PAYMENTS_MERCHANT } = ConfigObservable.config;

    return (
      this.paymentIntegrationAvailable &&
      TM3_PAYMENTS_ENABLE_TM3 === 'TRUE' &&
      TM3_PAYMENTS_MERCHANT.toLowerCase() === PaymentProviderName.ClearAccept.toLowerCase()
    );
  }

  @computed
  get getActiveAccountingSystemInfo() {
    const { TM3_ACCOUNTING_SYSTEM, TM3_ACCOUNTING_SYSTEM_ID, TM3_ACCOUNTING_SYSTEM_INFO } =
      ConfigObservable.config;
    if (
      (TM3_ACCOUNTING_SYSTEM as AccountingSystemProviderName) ===
        AccountingSystemProviderName.None ||
      !TM3_ACCOUNTING_SYSTEM_ID
    ) {
      return null;
    }
    const accountingConfig = JSON.parse(TM3_ACCOUNTING_SYSTEM_INFO);
    const accountingSettingData = (accountingConfig as AccountingSettingInfo[]).find(
      (x) => x.tenantId === TM3_ACCOUNTING_SYSTEM_ID
    );
    return accountingSettingData;
  }

  @computed
  get isAccountingIntegrationActive() {
    return (
      cache.hasAllowedModule(ModuleEnum.AccountingIntegrations) &&
      this.getActiveAccountingSystemInfo != null
    );
  }

  @computed
  get accountingSystemName() {
    const { TM3_ACCOUNTING_SYSTEM } = ConfigObservable.config;
    if (
      this.getActiveAccountingSystemInfo == null ||
      (TM3_ACCOUNTING_SYSTEM as AccountingSystemProviderName) === AccountingSystemProviderName.None
    ) {
      return AccountingSystemProviderName.None;
    }
    return TM3_ACCOUNTING_SYSTEM as AccountingSystemProviderName;
  }

  @computed
  get paymentProviderName() {
    const { TM3_PAYMENTS_MERCHANT } = ConfigObservable.config;
    return TM3_PAYMENTS_MERCHANT.charAt(0).toUpperCase() + TM3_PAYMENTS_MERCHANT.slice(1);
  }

  @computed
  get medipass() {
    const { MEDIPASS_ACTIVE } = ConfigObservable.config;
    return MEDIPASS_ACTIVE === 'TRUE';
  }

  @computed
  get medipassEnabled() {
    const { MEDIPASS_ENABLED } = ConfigObservable.config;
    return this.medipass && MEDIPASS_ENABLED === 'TRUE';
  }

  @computed
  get showZoomPro() {
    const { ZOOM_PRO } = ConfigObservable.config;
    return ZOOM_PRO.toUpperCase() === 'TRUE';
  }

  @computed
  get video() {
    const { VIDEO_INTEGRATION } = ConfigObservable.config;
    return VIDEO_INTEGRATION !== 'NONE';
  }

  @computed
  get showVideoLink() {
    const { VIDEO_INTEGRATION } = ConfigObservable.config;
    return this.video && VIDEO_INTEGRATION === 'OTHER';
  }

  @computed
  get automationsVNext() {
    return this.loaded && this.has([FeatureEnum.AUTOMATIONS_VNEXT]);
  }

  @computed
  get patientPortalEnabled() {
    const { CASEFLOW_PATIENT_PORTAL_ENABLED } = ConfigObservable.config;
    return (
      this.loaded &&
      this.has([FeatureEnum.CASEFLOW_PATIENT_PORTAL]) &&
      CASEFLOW_PATIENT_PORTAL_ENABLED !== 'FALSE'
    );
  }

  @computed
  get referralPortalEnabled() {
    const { CASEFLOW_REFERRAL_PORTAL_ENABLED } = ConfigObservable.config;
    return (
      this.loaded &&
      this.has([FeatureEnum.CASEFLOW_REFERRAL_PORTAL]) &&
      CASEFLOW_REFERRAL_PORTAL_ENABLED !== 'FALSE'
    );
  }

  @computed
  get editablePricingEnabled() {
    const { CASEFLOW_EDITABLE_PRICING_ENABLED } = ConfigObservable.config;

    return (
      this.loaded &&
      this.has([FeatureEnum.CASEFLOW_EDITABLE_PRICING]) &&
      CASEFLOW_EDITABLE_PRICING_ENABLED !== 'FALSE'
    );
  }

  @computed
  get preReferralInformation() {
    const { CASEFLOW_PREREFERRAL_INFORMATION_ENABLED } = ConfigObservable.config;

    return (
      this.loaded &&
      this.has([FeatureEnum.CASEFLOW_PREREFERRAL_INFORMATION]) &&
      CASEFLOW_PREREFERRAL_INFORMATION_ENABLED === 'TRUE'
    );
  }

  @computed
  get myClinicIntegration() {
    return this.loaded && this.has([FeatureEnum.MY_CLINIC_INTEGRATION]);
  }

  @computed
  get hasPaymentMerchantEnabled() {
    return this.stripeEnabled || this.squareEnabled || this.clearAcceptEnabled;
  }
  @computed
  get hasPremium(): boolean {
    const { license } = LicenseObservable;
    return this.loaded && !!license && license.get('isClickPlus');
  }
  @computed
  get isTrial(): boolean {
    const { license } = LicenseObservable;
    return this.loaded && !!license && license.get('isEvaluating');
  }
  @computed
  get isTrialOrPremium(): boolean {
    return this.isTrial || this.hasPremium;
  }

  @computed get hasNewReferralPortal() {
    return this.loaded && this.has([FeatureEnum.FF_NEW_REFERRAL_PORTAL]);
  }

  @computed
  get hasMailChimp() {
    const { ENABLED_MAILCHIMP_INTEGRATION } = ConfigObservable.config;
    return this.loaded && (ENABLED_MAILCHIMP_INTEGRATION ?? '').toUpperCase() === 'TRUE';
  }
}

const featureStore = new FeatureStore();
export default featureStore;
